// NOT IDEAL BUT FOR THE COOKIES TO SET PROPERLY ON LOCALHOST, WE NEED TO DEFAULT store.state.config.domain TO 'local.savingforcollege.com' in store/index.js
import { waitForGtag } from '@/lib/tracking-utils'

//
// Constants for experiment names
//

// CTA New Jersey experiment
export const ALTERNATIVE_CTA_COPY_NJ = 'alternative_cta_copy_nj_1'

// CPE South Carolina experiment
export const EXPERIMENT_CPE_SOUTH_CAROLINA = 'cpe_south_carolina'
export const EXPERIMENT_CPE_NEW_MEXICO = 'cpe_new_mexico'

// Currently running experiments
const experiments = [
	// Example of how to add an experiment:
	// To add a new experiment, uncomment the above object and modify it as follows:
	// 1. eventName: The name of the event to track this experiment (will always be 'ab_test_split')
	// 2. experimentName: A unique identifier for this experiment (use a constant if defined)
	// 3. variants: An array of variant names (e.g., ['A', 'B'] for an A/B test)
	// 4. splitCondition: A number between 0 and 1 representing the split between variants
	//    (e.g., 0.5 for a 50/50 split, 0.7 for a 70/30 split)
	{
		eventName: 'ab_test_split',
		experimentName: ALTERNATIVE_CTA_COPY_NJ,
		variants: ['A', 'B'],
		splitCondition: 0.5
	},
	{
		eventName: 'sc_529_split',
		experimentName: EXPERIMENT_CPE_SOUTH_CAROLINA,
		variants: ['redirect', 'cpe'],
		splitCondition: 100.0 // 100% will see the control (A), 0% will see the variant (B)
	},
	{
		eventName: 'nm_529_split',
		experimentName: EXPERIMENT_CPE_NEW_MEXICO,
		variants: ['redirect', 'cpe'],
		splitCondition: 0.0 // 0% will see the control (A), 100% will see the variant (B)
	}
]

/**
 * Assigns an experiment variant, tracks the event, and sets the cookie if necessary.
 * @param {Object} options - The options object.
 * @param {string} options.experimentName - The name of the experiment.
 * @param {object} options.cookie - The cookie instance to store the experiment variant.
 * @param {object} options.store - The Vuex store instance.
 * @param {number} options.expirationDays - The number of days until the cookie expires.
 * @returns {string|null} - The assigned experiment variant, or null if the experiment is not found.
 */
export function handleExperiment({ experimentName, cookie, store, expirationDays }) {
	const experiment = findExperimentByName(experimentName)

	if (!experiment) {
		console.error(`Experiment "${experimentName}" not found`)
		return null
	}

	return assignVariantAndFireEvent({ experiment, cookie, expirationDays, store })
}

/**
 * Finds an experiment by its name.
 * @param {string} experimentName - The name of the experiment.
 * @returns {object|null} - The experiment object, or null if not found.
 */
function findExperimentByName(experimentName) {
	return experiments.find(experiment => experiment.experimentName === experimentName)
}

/**
 * Assigns a variant to the experiment based on the split condition, tracks the event, and sets the cookie.
 * @param {object} experiment - The experiment object.
 * @param {object} cookie - The cookie instance to store the experiment variant.
 * @param {object} store - The Vuex store instance.
 * @param {number} expirationDays - The number of days until the cookie expires.
 * @returns {string} - The assigned experiment variant.
 */
function assignVariantAndFireEvent({ experiment, cookie, expirationDays = 365, store }) {
	const { variants, splitCondition, experimentName, eventName } = experiment
	const storedVariant = getVariantNameFromCookie(experimentName, cookie)

	if (storedVariant) {
		console.log(`Already stored variant ${storedVariant} for experiment ${experimentName}`)
		return storedVariant
	}

	const randomValue = Math.random()
	let cumulativeProbability = 0

	const splitConditions = [splitCondition, 1 - splitCondition]
	console.log('domain', store.state.config.domain)

	for (let i = 0; i < variants.length; i++) {
		cumulativeProbability += splitConditions[i]
		if (randomValue < cumulativeProbability) {
			const variant = variants[i]

			cookie.set(computeCookieName(experimentName), variant, {
				maxAge: expirationDays * 24 * 60 * 60,
				domain: store.state.config.domain,
				path: '/'
			})
			waitForGtag()
				.then(() => {
					gtag('event', eventName, { exp_name: experimentName, group: variant })

					console.log(
						`Cookie set and event fired for variant ${variant} and experiment ${experimentName}`
					)
				})
				.catch(error => {
					// Error: `gtag` not yet available
					console.error('Error:', error.message)
				})

			return variant
		}
	}
}

/**
 * Retrieves the experiment variant from the cookie.
 * @param {string} experimentName - The name of the experiment.
 * @param {object} cookie - The cookie instance.
 * @returns {string|null} - The experiment variant, or null if not found.
 */
function getVariantNameFromCookie(experimentName, cookie) {
	return cookie.get(computeCookieName(experimentName))
}

/**
 * Computes the name of the cookie for the experiment.
 * @param {string} experimentName - The name of the experiment.
 * @returns {string} - The name of the cookie.
 */
function computeCookieName(experimentName) {
	return `${experimentName}_experiment`
}
